/**
 * Generic validation utilities
 */

/**
 * Normalizes an email address for lookups/storage — trimmed and lowercased
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Whether a field carries a real value — whitespace-only strings count as missing. */
export function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/** 6-digit hex color, e.g. `#3B5BDB`. */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX_COLOR_PATTERN.test(value);
}

/**
 * Validates email address format
 */
export function validateEmail(email: string | undefined): string | null {
  if (!email) return null;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Invalid email address format';
  }

  return null;
}

/**
 * Validates a non-empty string with optional max length
 */
export function validateNonEmptyString(
  value: string,
  fieldName: string,
  maxLength?: number
): string | null {
  if (!value || value.trim().length === 0) {
    return `${fieldName} is required`;
  }

  if (maxLength && value.length > maxLength) {
    return `${fieldName} is too long (maximum ${maxLength} characters)`;
  }

  return null;
}

/**
 * Validates a numeric string with optional decimal places
 */
export function validateNumericString(
  value: string,
  fieldName: string,
  options?: {
    maxDecimals?: number;
    min?: number;
    max?: number;
  }
): string | null {
  const {maxDecimals = 2, min, max} = options || {};

  const decimalPattern = maxDecimals > 0 ? `(\\.\\d{1,${maxDecimals}})?` : '';
  const regex = new RegExp(`^\\d+${decimalPattern}$`);

  if (!regex.test(value)) {
    const decimalMsg = maxDecimals > 0 ? ` with maximum ${maxDecimals} decimal places` : '';
    return `${fieldName} must be a valid number${decimalMsg}`;
  }

  const numValue = parseFloat(value);

  if (min !== undefined && numValue <= min) {
    return `${fieldName} must be greater than ${min}`;
  }

  if (max !== undefined && numValue > max) {
    return `${fieldName} exceeds maximum allowed value (${max.toLocaleString()})`;
  }

  return null;
}
